import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePricingContext } from '../../hooks/usePricingContext';
import { formatPrice } from '../../utils/format';
import { pdpWholesaleTier, TEST_IDS } from '../../utils/testIds';
/**
 * The "Wholesale pricing" block shown to logged-in wholesalers on PDPs. Renders
 * either the explicit per-quantity tier table OR the flat default-discount line —
 * matching `pricing.tierTable` precedence on the server.
 *
 * Returns null when the viewer isn't an authenticated wholesaler, or when the
 * product has neither tiers nor a default discount; this lets PDPs drop the
 * component in unconditionally without their own gating logic.
 *
 * Testid: a single `pdpWholesaleCallout` per PDP. Bespoke PDPs that show two
 * different callout placements should be careful not to render this twice.
 */ export default function WholesaleCallout({ product, tone = 'light', className = '' }) {
    const { isWholesaler, isAuthenticated } = useCurrentUser();
    const pricing = usePricingContext(product);
    const hasTiers = pricing.tierTable.length > 0;
    const hasDefaultDiscount = pricing.defaultWholesaleDiscountPct > 0;
    if (!isAuthenticated || !isWholesaler || pricing.isInDevelopment || !hasTiers && !hasDefaultDiscount) {
        return null;
    }
    if (tone === 'dark') {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": TEST_IDS.pdpWholesaleCallout,
            className: `border border-clay-500/60 bg-obsidian-900/60 backdrop-blur-sm p-4 ${className}`,
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-[10px] font-mono uppercase tracking-[0.32em] text-ember-300 mb-2",
                    children: "Wholesale pricing"
                }),
                hasTiers ? /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-1 text-sm text-bone-100",
                    children: pricing.tierTable.map((t)=>/*#__PURE__*/ _jsxs("li", {
                            "data-testid": pdpWholesaleTier(t.minQuantity),
                            className: "flex justify-between gap-6",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-bone-200/80",
                                    children: [
                                        t.minQuantity,
                                        "+ units"
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium text-bone-50",
                                    children: [
                                        formatPrice(t.unitPriceCents),
                                        " / unit"
                                    ]
                                })
                            ]
                        }, t.id))
                }) : /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm text-bone-100",
                    children: [
                        pricing.defaultWholesaleDiscountPct,
                        "% off every unit"
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": TEST_IDS.pdpWholesaleCallout,
        className: `mt-5 border-2 border-green-200 bg-green-50 p-4 ${className}`,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-semibold uppercase tracking-wider text-green-800 mb-2",
                children: "Wholesale pricing"
            }),
            hasTiers ? /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 text-sm text-stone-900",
                children: pricing.tierTable.map((t)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": pdpWholesaleTier(t.minQuantity),
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-stone-600",
                                children: [
                                    t.minQuantity,
                                    "+ units"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-semibold",
                                children: [
                                    formatPrice(t.unitPriceCents),
                                    " / unit"
                                ]
                            })
                        ]
                    }, t.id))
            }) : /*#__PURE__*/ _jsxs("p", {
                className: "text-sm text-stone-900",
                children: [
                    pricing.defaultWholesaleDiscountPct,
                    "% off every unit"
                ]
            })
        ]
    });
}
